import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ExternalLink } from "lucide-react";
import { api, API_ORIGIN } from "@/lib/api";

const JURISDICTIONS = [
  { id: "eu", label: "EU (European Accessibility Act)" },
  { id: "uk", label: "UK (Accessibility Regulations 2018)" },
  { id: "ca", label: "Canada (AODA)" },
  { id: "us", label: "US (ADA + Section 508)" },
];

export default function ReportsTab({ siteId, clientEmail, siteName, clientJurisdiction }) {
  const [reports, setReports] = useState([]);
  // Starts from the client's jurisdiction, inherited through the site, and
  // falls back to EU when the client has none recorded. Still overridable per
  // document: a client can need a statement for a market they are entering.
  const [jurisdiction, setJurisdiction] = useState(clientJurisdiction || "eu");
  const [generating, setGenerating] = useState("");
  // Branded web report links (Agency): created per report, listed below.
  const [links, setLinks] = useState([]);
  const [expiry, setExpiry] = useState("");
  const [sharing, setSharing] = useState("");

  const load = useCallback(() => {
    api.get(`/sites/${siteId}/reports`).then((r) => setReports(r.data)).catch(() => toast.error("Could not load reports"));
    api.get(`/sites/${siteId}/report-links`).then((r) => setLinks(r.data)).catch(() => {});
  }, [siteId]);
  useEffect(() => { load(); }, [load]);

  const createLink = async (reportId) => {
    setSharing(reportId);
    try {
      const body = expiry ? { expires_in_days: Number(expiry) } : {};
      const r = await api.post(`/reports/${reportId}/share`, body);
      await navigator.clipboard.writeText(r.data.url).catch(() => {});
      toast.success("Web report link created and copied");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create the link");
    } finally {
      setSharing("");
    }
  };

  const revokeLink = async (linkId) => {
    try {
      await api.delete(`/report-links/${linkId}`);
      toast.success("Link revoked. It now shows an unavailable page.");
      load();
    } catch {
      toast.error("Could not revoke the link");
    }
  };

  const copyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy the link");
    }
  };

  const generate = async (type) => {
    setGenerating(type);
    try {
      await api.post(`/sites/${siteId}/reports`, {
        report_type: type,
        jurisdiction: type === "statement" ? jurisdiction : null,
      });
      toast.success("Report generated");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not generate report");
    } finally {
      setGenerating("");
    }
  };

  // Absolute on purpose: this is the API host, not the dashboard. Built from
  // API_ORIGIN rather than the raw build variable, which production leaves
  // empty, so the href stayed relative and the SPA router answered it with the
  // dashboard instead of the PDF.
  const shareUrl = (r) => `${API_ORIGIN}/api/v1/reports/shared/${r.share_token}`;

  const mailtoHref = (r) => {
    const subject = encodeURIComponent(`Accessibility documentation for ${siteName}: ${r.title}`);
    const body = encodeURIComponent(
      `Hello,\n\nPlease find the latest accessibility documentation for ${siteName}:\n\n${r.title}\n${shareUrl(r)}\n\nThe document opens as a PDF and reflects the current audit and remediation status.\n\nBest regards`
    );
    return `mailto:${clientEmail || ""}?subject=${subject}&body=${body}`;
  };

  const remove = async (id) => {
    await api.delete(`/reports/${id}`);
    toast.success("Report deleted");
    load();
  };

  return (
    <div data-testid="reports-panel">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-white border border-gray-200 rounded-sm p-5">
          <h2 className="font-heading text-base font-semibold mb-1">Conformance report</h2>
          <p className="text-sm text-ink-muted mb-4">Full evaluation with methodology, EN 301 549 alignment table, issue register, and manual review results.</p>
          <button data-testid="generate-conformance-btn" onClick={() => generate("conformance")} disabled={!!generating}
            className="bg-[#16324F] text-white rounded-sm px-4 py-2 text-sm hover:bg-[#10243a] transition-colors disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F] focus-visible:ring-offset-2">
            {generating === "conformance" ? "Generating PDF..." : "Generate conformance report"}
          </button>
        </div>
        <div className="bg-white border border-gray-200 rounded-sm p-5">
          <h2 className="font-heading text-base font-semibold mb-1">Accessibility statement</h2>
          <p className="text-sm text-ink-muted mb-3">Jurisdiction-specific public statement with conformance status and enforcement procedure.</p>
          <label htmlFor="jurisdiction" className="block text-sm font-medium mb-1.5">Jurisdiction</label>
          <select id="jurisdiction" data-testid="jurisdiction-select" value={jurisdiction} onChange={(e) => setJurisdiction(e.target.value)}
            className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-[#16324F] outline-none mb-3">
            {JURISDICTIONS.map((j) => <option key={j.id} value={j.id}>{j.label}</option>)}
          </select>
          <button data-testid="generate-statement-btn" onClick={() => generate("statement")} disabled={!!generating}
            className="bg-[#16324F] text-white rounded-sm px-4 py-2 text-sm hover:bg-[#10243a] transition-colors disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F] focus-visible:ring-offset-2">
            {generating === "statement" ? "Generating PDF..." : "Generate statement"}
          </button>
        </div>
      </div>

      <div className="table-card" tabIndex={0} role="region" aria-label="Generated reports">
        <table className="w-full min-w-[840px] text-left border-collapse text-sm" data-testid="reports-table">
          <caption className="sr-only">Generated reports</caption>
          <thead>
            <tr className="border-b border-gray-300">
              <th scope="col" className="px-5 py-3 font-heading font-semibold">Document</th>
              <th scope="col" className="px-5 py-3 font-heading font-semibold text-right">Generated</th>
              <th scope="col" className="px-5 py-3 font-heading font-semibold text-right">Size</th>
              <th scope="col" className="px-5 py-3 font-heading font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {reports.length === 0 && (
              <tr><td colSpan={4} className="px-5 py-10 text-center text-ink-muted" data-testid="reports-empty">No reports generated yet.</td></tr>
            )}
            {reports.map((r) => (
              <tr key={r.id} className="border-b border-gray-200 last:border-0" data-testid={`report-row-${r.id}`}>
                <td className="px-5 py-3">
                  <p className="font-medium">{r.title}</p>
                  <p className="font-mono text-xs text-ink-muted">{r.report_type}{r.jurisdiction ? ` / ${r.jurisdiction.toUpperCase()}` : ""}</p>
                </td>
                <td className="px-5 py-3 font-mono text-xs text-right text-ink-muted">{new Date(r.created_at).toLocaleString()}</td>
                <td className="px-5 py-3 font-mono text-xs text-right">{(r.size_bytes / 1024).toFixed(0)} KB</td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  <a data-testid={`report-download-${r.id}`} href={shareUrl(r)} target="_blank" rel="noreferrer"
                    className="text-xs text-[#16324F] underline mr-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F] rounded-sm">
                    Download PDF
                  </a>
                  <a data-testid={`report-deliver-${r.id}`} href={mailtoHref(r)}
                    className="text-xs bg-[#16324F] text-white rounded-sm px-2.5 py-1.5 mr-3 hover:bg-[#10243a] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F] focus-visible:ring-offset-2">
                    Deliver to client
                  </a>
                  <button data-testid={`report-share-${r.id}`} onClick={() => createLink(r.id)} disabled={sharing === r.id}
                    className="text-xs border border-[#16324F] text-[#16324F] rounded-sm px-2.5 py-1 mr-3 hover:bg-[#16324F] hover:text-white transition-colors disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F] focus-visible:ring-offset-2">
                    {sharing === r.id ? "Creating..." : "Create web link"}
                  </button>
                  <button data-testid={`report-delete-${r.id}`} onClick={() => remove(r.id)}
                    className="text-xs text-[#C62828] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F] rounded-sm px-1">
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!clientEmail && reports.length > 0 && (
        <p className="text-xs text-ink-muted mt-2">Tip: add a contact email to this client so "Deliver to client" pre-addresses the email.</p>
      )}

      <div className="bg-white border border-gray-200 rounded-sm mt-6 p-5" data-testid="report-links-panel">
        <h2 className="font-heading text-base font-semibold mb-1">Branded web report links</h2>
        <p className="text-sm text-ink-muted mb-3">
          A web version of the report your client can open and revisit at an unguessable URL,
          carrying your logo and brand color. Agency plan. Links can expire or be revoked at any time.
        </p>
        <label htmlFor="link-expiry" className="block text-sm font-medium mb-1.5">Expiry for new links</label>
        <select id="link-expiry" data-testid="link-expiry-select" value={expiry} onChange={(e) => setExpiry(e.target.value)}
          className="border border-gray-300 rounded-sm px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-[#16324F] outline-none mb-4">
          <option value="">No expiry</option>
          <option value="30">30 days</option>
          <option value="90">90 days</option>
        </select>
        {links.length === 0 ? (
          <p className="text-sm text-ink-muted" data-testid="report-links-empty">
            No web links yet. Use "Create web link" next to a report above.
          </p>
        ) : (
          <ul className="divide-y divide-gray-200">
            {links.map((l) => (
              <li key={l.id} className="py-3 flex flex-wrap items-center gap-3" data-testid={`report-link-row-${l.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{l.title}</p>
                  <p className="font-mono text-xs text-ink-muted break-all">{l.url}</p>
                  <p className="font-mono text-[10px] text-ink-subtle mt-1">
                    Created {new Date(l.created_at).toLocaleDateString()}
                    {l.expires_at ? ` | expires ${new Date(l.expires_at).toLocaleDateString()}` : " | no expiry"}
                    {l.revoked_at ? " | revoked" : ""}
                  </p>
                </div>
                {!l.revoked_at && (
                  <>
                    <a data-testid={`report-link-open-${l.id}`} href={l.url} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs border border-[#16324F] text-[#16324F] rounded-sm px-2.5 py-1 hover:bg-[#16324F] hover:text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F] focus-visible:ring-offset-2">
                      Open <ExternalLink size={12} strokeWidth={1.8} aria-hidden="true" />
                    </a>
                    <button data-testid={`report-link-copy-${l.id}`} onClick={() => copyLink(l.url)}
                      className="text-xs text-[#16324F] underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F] rounded-sm px-1">
                      Copy
                    </button>
                    <button data-testid={`report-link-revoke-${l.id}`} onClick={() => revokeLink(l.id)}
                      className="text-xs text-[#C62828] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F] rounded-sm px-1">
                      Revoke
                    </button>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
